import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, authHeaders, fmtDM, PROJECTS, PROJECT_COLOR, salesmanColor } from './marketingShared';
import { CATEGORICAL } from '../constants/palette';
import './Marketing.css';

// Enquiries, Top Project, Junk Leads, Top Channel — drawn from the shared
// categorical ramp so KPI tile colour matches the rest of the app.
const KPI_COLORS = [CATEGORICAL[1], CATEGORICAL[4], CATEGORICAL[3], CATEGORICAL[0]];
const TABS = [
  { k: 'proj', label: 'By Project' },
  { k: 'sales', label: 'By Salesperson' },
  { k: 'cp', label: 'Channel × Project' },
  { k: 'sp', label: 'Salesperson × Project' },
];

function MarketingLeadReport({ month }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('proj');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get(`/report?month=${month}`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) { setError('Failed to load lead report'); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="mk-page"><div className="mk-error">{error}</div></div>;
  if (!data) return <div className="mk-page"><div className="mk-loading">Loading lead report…</div></div>;

  const k = data.kpis;
  const topCh = k.topChannel;
  const topProj = [...data.byProject].sort((a, b) => b.count - a.count)[0];
  const salesCols = data.bySalesman.map((s) => s.salesman);
  const salesTotals = Object.fromEntries(data.bySalesman.map((s) => [s.salesman, s.count]));
  const projTotals = Object.fromEntries(data.byProject.map((p) => [p.project, p.count]));
  const chColor = (c) => ({ FB: '#1877f2', Website: 'var(--rmc-green)', INSTA: '#e1306c' }[c] || 'var(--rmc-ink3)');

  // shared header for project-columned tables
  const projHead = (firstLabel) => (
    <thead><tr><th>{firstLabel}</th>{PROJECTS.map((p) => <th key={p}><span className="th-proj"><span className="dot" style={{ background: PROJECT_COLOR[p] }} />{p}</span></th>)}<th className="col-total">Total</th></tr></thead>
  );
  const projTotalRow = (
    <tr className="total">
      <td>Total</td>
      {PROJECTS.map((p) => { const n = projTotals[p] || 0; return <td key={p} style={n ? { color: PROJECT_COLOR[p], fontWeight: 800 } : undefined}>{n}</td>; })}
      <td className="col-total">{k.enquiries}</td>
    </tr>
  );

  // ---- the four tables ----
  const projTable = (
    <table className="mk-tbl daily">
      {projHead('Date')}
      <tbody>
        {data.dailyByProject && data.dailyByProject.length ? data.dailyByProject.map((row) => (
          <tr key={row.date}>
            <td>{fmtDM(row.date)}</td>
            {PROJECTS.map((p) => { const v = row.byProject[p]; return <td key={p} style={v ? { color: PROJECT_COLOR[p], fontWeight: 700 } : undefined}>{v || <span className="na">—</span>}</td>; })}
            <td className="col-total">{row.total}</td>
          </tr>
        )) : <tr><td className="empty" colSpan={PROJECTS.length + 2}>No leads this month.</td></tr>}
        {data.dailyByProject && data.dailyByProject.length > 0 && projTotalRow}
      </tbody>
    </table>
  );

  const salesTable = (
    <table className="mk-tbl daily">
      <thead><tr><th>Date</th>{salesCols.map((s) => <th key={s}><span className="th-proj"><span className="dot" style={{ background: salesmanColor(s) }} />{s}</span></th>)}<th className="col-total">Total</th></tr></thead>
      <tbody>
        {data.dailyBySalesman && data.dailyBySalesman.length ? data.dailyBySalesman.map((row) => (
          <tr key={row.date}>
            <td>{fmtDM(row.date)}</td>
            {salesCols.map((s) => { const v = row.bySalesman[s]; return <td key={s} style={v ? { color: salesmanColor(s), fontWeight: 700 } : undefined}>{v || <span className="na">—</span>}</td>; })}
            <td className="col-total">{row.total}</td>
          </tr>
        )) : <tr><td className="empty" colSpan={salesCols.length + 2}>No leads this month.</td></tr>}
        {data.dailyBySalesman && data.dailyBySalesman.length > 0 && (
          <tr className="total">
            <td>Total</td>
            {salesCols.map((s) => <td key={s} style={{ color: salesmanColor(s), fontWeight: 800 }}>{salesTotals[s] || 0}</td>)}
            <td className="col-total">{k.enquiries}</td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const cpTable = (
    <table className="mk-tbl daily">
      {projHead('Channel')}
      <tbody>
        {data.byChannelProject && data.byChannelProject.length ? data.byChannelProject.map((row) => (
          <tr key={row.channel}>
            <td><span className="chip"><span className="dot" style={{ background: chColor(row.channel) }} />{row.channel}</span></td>
            {PROJECTS.map((p) => { const v = row.byProject[p]; return <td key={p} style={v ? { color: PROJECT_COLOR[p], fontWeight: 700 } : undefined}>{v || <span className="na">—</span>}</td>; })}
            <td className="col-total">{row.total}</td>
          </tr>
        )) : <tr><td className="empty" colSpan={PROJECTS.length + 2}>No leads this month.</td></tr>}
        {data.byChannelProject && data.byChannelProject.length > 0 && projTotalRow}
      </tbody>
    </table>
  );

  const spTable = (
    <table className="mk-tbl daily">
      {projHead('Salesperson')}
      <tbody>
        {data.bySalesmanProject && data.bySalesmanProject.length ? data.bySalesmanProject.map((row) => (
          <tr key={row.salesman}>
            <td><span className="chip"><span className="dot" style={{ background: salesmanColor(row.salesman) }} />{row.salesman}</span></td>
            {PROJECTS.map((p) => { const v = row.byProject[p]; return <td key={p} style={v ? { color: PROJECT_COLOR[p], fontWeight: 700 } : undefined}>{v || <span className="na">—</span>}</td>; })}
            <td className="col-total">{row.total}</td>
          </tr>
        )) : <tr><td className="empty" colSpan={PROJECTS.length + 2}>No leads this month.</td></tr>}
        {data.bySalesmanProject && data.bySalesmanProject.length > 0 && projTotalRow}
      </tbody>
    </table>
  );

  const activeTable = { proj: projTable, sales: salesTable, cp: cpTable, sp: spTable }[tab];

  return (
    <div className="mk-page">
      <div className="mk-head">
        <div>
          <div className="mk-crumbs">Marketing Report &rsaquo; <b>Lead Report</b></div>
          <h1>Lead Report</h1>
          <div className="sub">{month} · enquiry leads by project, salesperson &amp; channel</div>
        </div>
        <div className="mk-head-actions">
          <button className="mk-btn primary" onClick={() => navigate('/marketing/leads')}>Key in Lead →</button>
        </div>
      </div>

      <div className="mk-banner">
        <span>ⓘ</span>
        <span><b>Lead information only.</b> Rolls up from <b>Key in Lead</b>. Junk leads (suppliers/jobs) are recorded but excluded. Spend &amp; cost are on the <b>Spend Report</b>.</span>
      </div>

      {/* KPIs */}
      <div className="mk-kpis mk-kpis-four">
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[0] }}>
          <div className="k-label">Enquiries</div>
          <div className="k-val">{k.enquiries}</div>
          <div className="k-sub muted">{data.byProject.filter((r) => r.count > 0).length} of {PROJECTS.length} projects</div>
          <div className="k-note">{k.leadTarget > 0 ? `${Math.round((k.enquiries / k.leadTarget) * 100)}% of ${k.leadTarget} target` : 'real customer leads'}</div>
        </div>
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[3] }}>
          <div className="k-label">Top Channel</div>
          <div className={`k-val ${topCh ? '' : 'muted'}`} style={{ fontSize: 20 }}>{topCh ? topCh.channel : '—'}</div>
          <div className="k-sub muted">{topCh ? `${topCh.count} leads` : 'none yet'}</div>
          <div className="k-note">{topCh ? `${topCh.pct}% of enquiries` : '—'}</div>
        </div>
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[1] }}>
          <div className="k-label">Top Project</div>
          <div className={`k-val ${topProj && topProj.count ? '' : 'muted'}`} style={{ fontSize: 20 }}>{topProj && topProj.count ? topProj.project : '—'}</div>
          <div className="k-sub muted">{topProj && topProj.count ? `${topProj.count} leads` : 'none yet'}</div>
          <div className="k-note">{topProj && topProj.count ? `${topProj.pct}% of enquiries` : '—'}</div>
        </div>
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[2] }}>
          <div className="k-label">Junk Leads</div>
          <div className={`k-val ${k.junkLeads ? '' : 'muted'}`}>{k.junkLeads}</div>
          <div className="k-sub muted">excluded from counts</div>
          <div className="k-note">suppliers, job-seekers</div>
        </div>
      </div>

      {/* Leads by project (table) + by channel (bars) — like the Spend Report */}
      <div className="mk-row-2">
        <div className="mk-card">
          <div className="mk-card-head"><h3>Leads — by Project</h3></div>
          <div className="mk-tbl-scroll">
            <table className="mk-tbl">
              <thead><tr><th>Project</th><th>Enquiries</th><th>Share</th></tr></thead>
              <tbody>
                {data.perProject.map((r) => (
                  <tr key={r.project}>
                    <td><span className="chip"><span className="dot" style={{ background: PROJECT_COLOR[r.project] }} />{r.project}</span></td>
                    <td>{r.enquiries}</td>
                    <td>{r.enquiries > 0 ? `${r.enquiriesPct}%` : <span className="na">—</span>}</td>
                  </tr>
                ))}
                <tr className="total"><td>Total</td><td>{k.enquiries}</td><td>100%</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mk-foot">Actual enquiries per project, from <b>Key in Lead</b>. Share = % of total enquiries.</p>
        </div>

        <div className="mk-card">
          <div className="mk-card-head"><h3>Share of Enquiries — by Channel</h3><span className="c-sub">{month}</span></div>
          <div className="bd-list">
            {data.byChannel.length ? data.byChannel.map((c) => (
              <div className="bd-row" key={c.channel}>
                <div className="bd-top">
                  <span className="bd-name"><span className="dot" style={{ background: chColor(c.channel) }} />{c.channel}</span>
                  <span className="bd-val"><b>{c.count}</b> leads</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="tgt-bar"><div className="tgt-fill" style={{ width: `${Math.min(100, c.pct)}%`, background: chColor(c.channel) }} /></div>
                  <span className="tgt-pct">{c.pct}%</span>
                </div>
              </div>
            )) : <p className="mk-foot" style={{ margin: 0 }}>No leads this month.</p>}
          </div>
          <p className="mk-foot">Share of total enquiries by acquisition channel (junk excluded).</p>
        </div>
      </div>

      {/* Tabbed breakdowns — one at a time */}
      <div className="mk-card">
        <div className="mk-card-head"><h3>Lead Breakdowns</h3><span className="c-sub">{month} · junk excluded</span></div>
        <div className="mk-tabs">
          {TABS.map((t) => <button key={t.k} className={tab === t.k ? 'on' : ''} onClick={() => setTab(t.k)}>{t.label}</button>)}
        </div>
        <div className="mk-tbl-scroll">{activeTable}</div>
      </div>
    </div>
  );
}

export default MarketingLeadReport;
